import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { TOAST_OPTIONS, toToastMessage } from './toastUtils';

const IMPORT_BATCH_SIZE = 50;

const defaultGetId = (item) => item?.id;
const defaultGetSearchText = (item) => item?.text || '';

export function useCsvResourceTab({
  fetchItems,
  parseCsvFile,
  bulkCreate,
  exportCsv,
  deleteItem,
  entityLabel,
  getId = defaultGetId,
  getSearchText = defaultGetSearchText,
  enableSelection = false,
  fetchDetails,
  messages,
}) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const selectedItemId = enableSelection ? getId(selectedItem) : null;

  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [importProgress, setImportProgress] = useState(null);
  // importProgress: null (idle) | { done: number, total: number }

  const fileInputRef = useRef(null);

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const searchWords = search.toLowerCase().split(/\s+/).filter(Boolean);

    return items.filter((item) => {
      const text = String(getSearchText(item) || '').toLowerCase();
      return searchWords.every((w) => text.includes(w));
    });
  }, [items, search, getSearchText]);

  // Initial fetch
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.resolve()
      .then(() => fetchItems())
      .then((data) => {
        if (cancelled) return;
        const nextItems = Array.isArray(data) ? data : [];
        setItems(nextItems);

        if (enableSelection) {
          setSelectedItem((prev) => prev || (nextItems.length ? nextItems[0] : null));
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const message = messages?.loadFailed || `Failed to load ${entityLabel}`;
        toast.error(
          toToastMessage(err, message),
          TOAST_OPTIONS
        );
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchItems, enableSelection, entityLabel]);

  // Keep selection valid + in-sync with list references
  useEffect(() => {
    if (!enableSelection) return;
    if (!selectedItemId) return;

    const stillExists = filteredItems.find((it) => getId(it) === selectedItemId);

    if (!stillExists && filteredItems.length) setSelectedItem(filteredItems[0]);
    if (!stillExists && !filteredItems.length) setSelectedItem(null);

    if (stillExists && stillExists !== selectedItem) setSelectedItem(stillExists);
  }, [enableSelection, filteredItems, getId, selectedItem, selectedItemId]);

  // Optional: load details on selection
  useEffect(() => {
    if (!enableSelection) return;
    if (typeof fetchDetails !== 'function') return;

    let cancelled = false;
    if (!selectedItemId) {
      setDetails(null);
      return () => {
        cancelled = true;
      };
    }

    setDetailsLoading(true);
    Promise.resolve()
      .then(() => fetchDetails(selectedItemId))
      .then((res) => {
        if (cancelled) return;
        setDetails(res);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = messages?.detailsFailed || `Failed to load ${entityLabel} details`;
        toast.error(
          toToastMessage(err, message),
          TOAST_OPTIONS
        );
        setDetails(null);
      })
      .finally(() => {
        if (cancelled) return;
        setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enableSelection, entityLabel, fetchDetails, selectedItemId]);

  const handleClickImport = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  const handleImportCSV = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const inputEl = e.target; // capture before async gaps

      let imported;
      try {
        imported = await parseCsvFile(file);
      } catch (err) {
        toast.error(toToastMessage(err, 'Import failed'), TOAST_OPTIONS);
        inputEl.value = '';
        return;
      }

      if (!imported?.length) {
        inputEl.value = '';
        return;
      }

      const batches = [];
      for (let i = 0; i < imported.length; i += IMPORT_BATCH_SIZE) {
        batches.push(imported.slice(i, i + IMPORT_BATCH_SIZE));
      }

      setImportProgress({ done: 0, total: imported.length });
      const allCreated = [];

      try {
        for (const batch of batches) {
          // eslint-disable-next-line no-await-in-loop
          const created = await bulkCreate(batch);
          const newItems = created || [];
          allCreated.push(...newItems);
          setItems((prev) => [...prev, ...newItems]);
          setImportProgress({ done: allCreated.length, total: imported.length });
        }

        if (enableSelection) {
          setSelectedItem((prev) => prev || allCreated[0] || null);
        }

        const count = allCreated.length;
        toast.success(
          typeof messages?.imported === 'function'
            ? messages.imported(count)
            : `Imported ${count} ${entityLabel}`,
          TOAST_OPTIONS
        );
      } catch (err) {
        const partial = allCreated.length;
        const failed = imported.length - partial;
        toast.error(
          toToastMessage(
            err,
            partial > 0
              ? `Imported ${partial}, failed on batch (${failed} rows not imported)`
              : 'Import failed'
          ),
          TOAST_OPTIONS
        );
      } finally {
        setImportProgress(null);
        inputEl.value = '';
      }
    },
    [bulkCreate, enableSelection, entityLabel, messages?.imported, parseCsvFile]
  );

  const handleExportCSV = useCallback(async () => {
    try {
      const { count } = await exportCsv();
      const successMessage =
        typeof messages?.exported === 'function'
          ? messages.exported(count)
          : `Exported ${count} ${entityLabel}`;
      toast.success(successMessage, TOAST_OPTIONS);
    } catch (err) {
      toast.error(toToastMessage(err, 'Export failed'), TOAST_OPTIONS);
    }
  }, [entityLabel, exportCsv, messages?.exported]);

  const handleDelete = useCallback(
    (id) => {
      setPendingDeleteId(id);
    },
    []
  );

  const handleConfirmDelete = useCallback(
    async () => {
      const id = pendingDeleteId;
      setPendingDeleteId(null);
      if (!id) return;
      try {
        await deleteItem(id);
        setItems((prev) => prev.filter((it) => getId(it) !== id));

        if (enableSelection) {
          setSelectedItem((prev) => (getId(prev) === id ? null : prev));
        }

        toast.success(messages?.deleted || 'Deleted', TOAST_OPTIONS);
      } catch (err) {
        toast.error(toToastMessage(err, 'Delete failed'), TOAST_OPTIONS);
      }
    },
    [deleteItem, enableSelection, getId, messages?.deleted, pendingDeleteId]
  );

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const handleSelect = useCallback(
    (item) => {
      if (!enableSelection) return;
      setSelectedItem(item);
    },
    [enableSelection]
  );

  const reloadDetails = useCallback(async () => {
    if (!enableSelection) return null;
    if (typeof fetchDetails !== 'function') return null;
    if (!selectedItemId) return null;

    setDetailsLoading(true);
    try {
      const res = await fetchDetails(selectedItemId);
      setDetails(res);
      return res;
    } catch (err) {
      const message = messages?.detailsFailed || `Failed to load ${entityLabel} details`;
      toast.error(
        toToastMessage(err, message),
        TOAST_OPTIONS
      );
      setDetails(null);
      return null;
    } finally {
      setDetailsLoading(false);
    }
  }, [enableSelection, entityLabel, fetchDetails, messages?.detailsFailed, selectedItemId]);

  return {
    items,
    setItems,
    filteredItems,
    search,
    setSearch,
    loading,

    enableSelection,
    selectedItem,
    setSelectedItem,
    selectedItemId,

    details,
    detailsLoading,

    fileInputRef,
    handleClickImport,
    handleImportCSV,
    importProgress,
    handleExportCSV,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
    pendingDeleteId,
    handleSelect,
    reloadDetails,
  };
}
